import { useSelector } from 'react-redux';
import { INSTANCE_SLUGS } from '../instances/instanceSlugs';
import { isProfileInInstance, useIsInstance } from '../instances/useInstance';
import { examsSelectors } from '../slices/exams/examsSlice';
import { isPreclinicalExam } from '../utils/examUtils';
import type { RootState } from '../store/store';
import { useResetMcGrawHillModeOutsideInstance } from './mcgrawHillModeStore';
import { usePlainOraViewFlag } from './plainOraView';

/**
 * Whether the signed-in user belongs to the McGraw Hill instance. It also puts
 * the Clinical / Study mode back to Clinical whenever the answer is no
 * (ORA-1260): this hook is read on every chat surface and on the AI desktop,
 * so a mode chosen as a member never outlives the membership.
 */
export const useIsMcGrawHill = (): boolean => {
  const isMcGrawHill = useIsInstance(INSTANCE_SLUGS.mcgrawHill);
  useResetMcGrawHillModeOutsideInstance(isMcGrawHill);
  return isMcGrawHill;
};

/**
 * The custom exams that are McGraw Hill textbooks: Katzung's Basic & Clinical
 * Pharmacology, the catalog's one working book. Any other custom exam a member
 * holds (the DUT Nursing Handbook, ORA-1247) is an ordinary preclinical exam.
 */
export const MCGRAW_HILL_TEXTBOOK_EXAM_IDS: readonly string[] = [
  '70a03d12-25ea-4be8-be96-d2edda495762',
];

/** The DUT Nursing Handbook 2026 exam every member is enrolled in (ORA-1247). */
export const MCGRAW_HILL_HANDBOOK_EXAM_ID =
  'e5789f3b-b47f-418c-a70e-f4448def4e7f';

/**
 * Whether the store says a McGraw Hill member is studying a textbook:
 * Preclinical primary with a McGraw Hill textbook as the active custom exam.
 * Read defensively, as `useInstance` does: a narrow store may carry no user
 * or exams slice.
 */
export const selectIsMcGrawHillTextbook = (
  state: Partial<RootState>
): boolean => {
  if (!isProfileInInstance(state.user?.profile, INSTANCE_SLUGS.mcgrawHill)) {
    return false;
  }

  if (!state.exams) return false;
  const fullState = state as RootState;
  const primaryExam = examsSelectors.selectPrimaryExam(fullState);
  const activeCustomExam = examsSelectors.selectActiveCustomExam(fullState);

  return (
    isPreclinicalExam(primaryExam) &&
    MCGRAW_HILL_TEXTBOOK_EXAM_IDS.includes(activeCustomExam?.id ?? '')
  );
};

/**
 * Whether a McGraw Hill member is studying a McGraw Hill textbook (see
 * `selectIsMcGrawHillTextbook`). Gates the chapter viewer's skin (its
 * full-page chrome); the Dashboard is Ora's own. The brand (logo, theme,
 * chat) stays on `useIsMcGrawHill`, and any other exam looks as it does for
 * everyone.
 */
export const useIsMcGrawHillTextbook = (): boolean => {
  // The plain Ora view (ORA-1268) flips the answer without a store change:
  // re-render on it, with an inline selector so react-redux runs it again.
  usePlainOraViewFlag();
  return useSelector((state: RootState) => selectIsMcGrawHillTextbook(state));
};

/** The McGraw Hill catalog (textbook selection); `/mh/<category>` for one. */
export const MCGRAW_HILL_PATH = '/mh';

/** mcgraw-hill (ORA-1189): the demo's root, the launcher the logo opens. */
export const MCGRAW_HILL_ROOT = '/mh-demos';
